import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getLatestPodcastEpisodes,
  PROF_BAS_BLOEM_WEEKLY_SHOW_ID,
} from '@domain/features/podcasts/podcasts-feature'
import type { PodcastEpisode } from '@domain/models/podcast'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'

export function useHomePodcasts(count = 3) {
  const ssrEpisodes = useSsrPageData()?.home?.latestPodcasts?.slice(0, count)
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>(ssrEpisodes ?? [])
  const [loading, setLoading] = useState(!ssrEpisodes)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('home')

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    refresh: async ({ reason }) => {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      const showInitialLoading = reason === 'mount' && episodes.length === 0

      if (showInitialLoading) {
        setLoading(true)
      }

      try {
        setError(null)
        const fetchPolicy = reason === 'mount' ? 'default' : 'server'
        const result = await getLatestPodcastEpisodes(
          PROF_BAS_BLOEM_WEEKLY_SHOW_ID,
          count,
          fetchPolicy,
        )
        if (requestId === requestIdRef.current) {
          setEpisodes(result)
        }
      } catch (loadError) {
        if (requestId === requestIdRef.current) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load podcast episodes.',
          )
        }
        throw loadError
      } finally {
        if (showInitialLoading && requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  useEffect(() => {
    void triggerRefresh('mount', { force: true })
  }, [triggerRefresh])

  const refresh = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  return {
    episodes,
    loading,
    error,
    refresh,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
